import React, { useEffect, useMemo, useCallback, useState } from 'react';
import styles from '../MainPages.module.css';
import CardPlaceholder from '../../Components/UILibrary/CardPlaceholder';
import ManageProductCard from '../../Components/Products/ManageProductCard';
import { CategoriesService } from '../../../Logic/CategoriesService';
import { useNavigate, useParams } from 'react-router-dom';
import SearchBar from '../../Components/UILibrary/SearchBar';
import { SearchItemType } from '@model/Search.model';
import type { CategoriesMeta } from '@model/Category.model';
import { ProductsService } from '../../../Logic/ProductsService';
import { UserAuthenticationStoreImplementation } from 'Data/UserAuthentication/UserAuthenticationStoreImplementation';
import { useForceLogoutAndRedirect } from '../../../Logic/Functions/Hooks/useForceLogoutAndRedirect';
import { UnauthorizedPopupStoreImplementation } from 'Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation';
import ConfirmModal from '../../Components/Modals/ConfirmModal';
import { IProductData } from '@model/Products.model';

const ManageProductPage: React.FC = () => {
  const navigate = useNavigate();
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [categoryTitle, setCategoryTitle] = useState<string>('');
  // TODO: Fix typing
  const [products, setProducts] = useState<any[]>([]);
  const [meta, setMeta] = useState<CategoriesMeta>({
    total: 0,
    page: 1,
    perPage: 10,
    pages: 0,
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const requestParams = useMemo(
    () => ({ page: meta.page, perPage: meta.perPage }),
    [meta.page, meta.perPage]
  );
  const [categoryId, setCategoryId] = useState<string>('');
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const authStore = UserAuthenticationStoreImplementation();
  const onAuthError = useForceLogoutAndRedirect();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState<string>('');

  const categoriesService = new CategoriesService();
  const productsService = new ProductsService();

  const filteredProducts = useMemo(() => {
    return productsService.filterProductsBySearch(products, searchQuery);
  }, [productsService, products, searchQuery]);

  useEffect(() => {
    categoriesService.handleGetCategoryBySlugAdmin({
      slug: categorySlug,
      page: requestParams.page,
      perPage: requestParams.perPage,
      token: authStore.authenticationState?.token ?? '',
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
      setLoading,
      setApiError,
      setCategory: (cat) => {
        setCategoryTitle(cat?.title ?? '');
        setCategoryId(cat?.id ?? '');
      },
      setProducts,
      setMeta,

    });
  }, [categorySlug, requestParams.page, requestParams.perPage]);

  const handleConfirmDelete = useCallback(async () => {
    await productsService.handleDeleteProduct({
      productId: deleteId,
      token: authStore.authenticationState?.token ?? '',
      setLoading,
      setApiError,
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
      onSuccess: () => {
        setDeleteId(null);
        setDeleteName('');
        window.location.reload();
      },
    });
  }, [deleteId, authStore.authenticationState?.token, productsService]);

  return (
    <main className={styles.Page} data-testid="mp-page">
      <header className={styles.Header} data-testid="mp-header">
        <div className={styles.HeaderLeft} data-testid="mp-header-left">
          <button
            type="button"
            className={styles.BackBtn}
            onClick={() => navigate('/admin/manage-categories')}
            data-testid="mp-back-to-categories"
          >
            ← Back to Categories
          </button>
        </div>

        <h1 className={styles.Title} data-testid="mp-title">
          {categoryTitle || 'Products'}
        </h1>

        <div className={styles.HeaderRight} data-testid="mp-header-right">
          <button
            className={styles.AddBtn}
            type="button"
            disabled={!categoryId}
            onClick={() =>
              navigate(`/admin/categories/${encodeURIComponent(categoryId)}/new-product`)
            }
            data-testid="mp-add-product"
          >
            Add Product
          </button>
        </div>
      </header>

      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search products..."
        typesFilter={['product'] as SearchItemType[]}
        enableDropdown={false}
        data-testid="mp-searchbar"
      />
      {/* 
            <div className={styles.mcSearchRow} data-testid="mp-search-row">
                <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search products..."
                    typesFilter={["product"] as SearchItemType[]}
                    enableDropdown={false}
                    data-testid="mp-searchbar"
                />
            </div>

            <div className={styles.mcMetaRow} data-testid="mp-meta-row">
                <p className={styles.mcSubtitle} data-testid="mp-total">
                    {typeof totalCount === "number" ? `Total: ${totalCount}` : "Showing products"}
                </p>

                <div className={styles.mcControls} data-testid="mp-controls">
                    <div
                        ref={perPageDropdownWrapRef}
                        className={styles.mcPpdWrap}
                        data-testid="mp-perpage-wrap"
                    >
                        <span className={styles.mcPpdLabel}>Per page</span>

                        <button
                            ref={perPageDropdownBtnRef}
                            type="button"
                            className={styles.mcPpdButton}
                            aria-haspopup="listbox"
                            aria-expanded={perPageDropdownOpen}
                            aria-controls="mp-perpage-menu"
                            onClick={togglePerPageDropdown}
                            onKeyDown={onPerPageDropdownButtonKeyDown}
                            data-testid="mp-perpage-button"
                        >
                            {perPage}
                            <span className={styles.mcPpdChevron} aria-hidden />
                        </button>

                        {perPageDropdownOpen && (
                            <ul
                                id="mp-perpage-menu"
                                ref={perPageDropdownListRef}
                                role="listbox"
                                tabIndex={-1}
                                className={styles.mcPpdMenu}
                                onKeyDown={onPerPageDropdownListKeyDown}
                                aria-activedescendant={`mp-ppd-opt-${perPageDropdownActiveIndex}`}
                                data-testid="mp-perpage-menu"
                            >
                                {perPageOptions.map((option: number, index: number) => {
                                    const isSelected = option === perPage;
                                    const isActive = index === perPageDropdownActiveIndex;

                                    return (
                                        <li
                                            id={`mp-ppd-opt-${index}`}
                                            key={option}
                                            role="option"
                                            aria-selected={isSelected}
                                            className={[
                                                styles.mcPpdOption,
                                                isSelected ? styles.mcPpdSelected : "",
                                                isActive ? styles.mcPpdActive : "",
                                            ].join(" ")}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => selectPerPageAt(index)}
                                            data-testid={`mp-perpage-option-${option}`}
                                        >
                                            {option}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {totalPages && (
                        <div className={styles.mcPager} data-testid="mp-pager">
                            <button
                                onClick={() => setPage(Math.max(1, page - 1))}
                                disabled={page <= 1}
                                data-testid="mp-pager-prev"
                            >
                                Prev
                            </button>

                            <span className={styles.mcPageIndicator} data-testid="mp-pager-indicator">
                                {page} / {totalPages}
                            </span>

                            <button
                                onClick={() => setPage(Math.min(totalPages, page + 1))}
                                disabled={page >= totalPages}
                                data-testid="mp-pager-next"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div> */}

      {loading && (
        <div data-testid="mp-loading">
          <CardPlaceholder count={6} customWrapperStyle={styles.Listproducts} />
        </div>
      )}

      {!loading && apiError && (
        <div className={styles.Error} role="alert" data-testid="mp-error">
          {apiError}
        </div>
      )}

      {!loading && !apiError && filteredProducts.length === 0 && (
        <div className={styles.Empty} data-testid="mp-empty">
          No products found.
        </div>
      )}

      {!loading && !apiError && filteredProducts.length > 0 && (
        <section className={styles.Listproducts} data-testid="mp-list">
          {filteredProducts.map((p: IProductData) => (
            <ManageProductCard
              key={p.id}
              id={p.id}
              name={p.name}
              slug={p.slug}
              description={p.shortDescription ?? ''}
              images={p.images ?? null}
              onEdit={(slug) => navigate(`/admin/products/${encodeURIComponent(slug)}/edit`)}
              onDelete={(id: string) => {
                setDeleteId(id);
                setDeleteName(p.name);
                setConfirmOpen(true);
              }}
            />
          ))}
        </section>
      )}

      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={handleConfirmDelete}
        message={
          deleteName
            ? `Are you sure you want to delete the product "${deleteName}"?`
            : 'Are you sure you want to delete this product?'
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </main>
  );
};

export default ManageProductPage;
